import type { ConditionCode } from '../../shared/contract'

/** Sky Theatre's presentation families. The engine/wire condition set stays unchanged. */
export const WX_ICON_CODES = [
  'clear', 'mostly-clear', 'partly-cloudy', 'mostly-cloudy', 'overcast',
  'fog', 'haze', 'smoke', 'dust', 'wind', 'drizzle', 'light-rain', 'rain',
  'heavy-rain', 'showers', 'thunderstorm', 'severe-storm', 'light-snow',
  'snow', 'heavy-snow', 'snow-showers', 'blowing-snow', 'wintry-mix',
  'freezing-drizzle', 'freezing-rain', 'sleet', 'hail', 'tornado',
  'tropical-storm', 'hurricane', 'hot', 'cold',
] as const
export type WxIconCode = (typeof WX_ICON_CODES)[number]
export type WxCondition = ConditionCode | WxIconCode

export const CONDITION_ICON: Record<ConditionCode, WxIconCode> = {
  clear: 'clear', mostly_clear: 'mostly-clear', partly_cloudy: 'partly-cloudy',
  mostly_cloudy: 'mostly-cloudy', cloudy: 'overcast', fog: 'fog', haze: 'haze',
  smoke: 'smoke', dust: 'dust', breezy: 'wind', windy: 'wind', drizzle: 'drizzle',
  light_rain: 'light-rain', rain: 'rain', heavy_rain: 'heavy-rain', showers: 'showers',
  scattered_showers: 'showers', thunderstorms: 'thunderstorm',
  scattered_thunderstorms: 'thunderstorm', severe_thunderstorms: 'severe-storm',
  flurries: 'light-snow', light_snow: 'light-snow', snow: 'snow', heavy_snow: 'heavy-snow',
  snow_showers: 'snow-showers', blizzard: 'blowing-snow', sleet: 'sleet',
  freezing_drizzle: 'freezing-drizzle', freezing_rain: 'freezing-rain', wintry_mix: 'wintry-mix',
  hail: 'hail', hot: 'hot', frigid: 'cold', tropical_storm: 'tropical-storm',
  hurricane: 'hurricane',
}

export function wxIconCode(condition: WxCondition): WxIconCode {
  return (Object.hasOwn(CONDITION_ICON, condition) ? CONDITION_ICON[condition as ConditionCode] : undefined) ??
    (WX_ICON_CODES.includes(condition as WxIconCode) ? condition as WxIconCode : 'overcast')
}

export type WxOpticalSize = 'regular' | 'compact'

/** Sprite symbol convention, shared by the React component and direct DOM bindings. */
export function wxIconId(condition: WxCondition, night = false, optical: WxOpticalSize = 'regular'): string {
  return `wx-v5-${wxIconCode(condition)}-${night ? 'night' : 'day'}${optical === 'compact' ? '-compact' : ''}`
}

/** An opaque sky has no visible sun/moon. Day/night intentionally share these silhouettes. */
export const WX_CELESTIAL_CODES: readonly WxIconCode[] = [
  'clear', 'mostly-clear', 'partly-cloudy', 'mostly-cloudy', 'haze', 'dust',
  'showers', 'snow-showers',
]

export const WX_GLYPH_CODES = [
  'wind-direction', 'humidity', 'uv', 'pressure', 'visibility', 'air-quality',
  'sunrise', 'sunset', 'umbrella', 'places', 'search', 'radar', 'settings',
  'advisory', 'watch', 'warning', 'emergency', 'unknown',
  'moon-new', 'moon-waxing-crescent', 'moon-first-quarter', 'moon-waxing-gibbous',
  'moon-full', 'moon-waning-gibbous', 'moon-last-quarter', 'moon-waning-crescent',
  'wind-gust', 'compass', 'temperature', 'calendar',
] as const
export type WxGlyphCode = (typeof WX_GLYPH_CODES)[number]

export function wxGlyphId(glyph: WxGlyphCode, optical: WxOpticalSize = 'regular'): string {
  return `wx-v5-${glyph}${optical === 'compact' ? '-compact' : ''}`
}
